// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import pick from 'lodash/pick'
import { withStyles } from '@material-ui/core/styles'
import config from 'config'

// Utils
import { getTermsURL } from 'utils/agreement'

// Validation
import { validateRequiredString, validateEmail, validateMinLength } from 'utils/validation'
import { Validator, ValidationError } from 'react-vld'

// Components
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'
import Checkbox from '@material-ui/core/Checkbox'
import Fade from '@material-ui/core/Fade'
import { ScrollSection } from 'atoms/ScrollSections'
import { StyledLink } from 'atoms/Link'
import StepContainer from './StepContainer'
import StepTitle from './StepTitle'
import StepSpacer from './StepSpacer'
import StepInput from './StepInput'
import StepButton from './StepButton'
import SocialAccount from './AccountStepSocial'

// Styles
const styles = theme => ({
  checkboxError: {
    color: theme.palette.error.main
  }
})

export default
@withStyles(styles)
class OnboardingAccountStep extends Component {
  static propTypes = {
    formData: PropTypes.object.isRequired,
    onFormDataUpdate: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    externalAcc: PropTypes.object,
    classes: PropTypes.object.isRequired,
  }

  constructor({ formData }) {
    super()
    this.state = {
      emailFocused: false,
      ...pick(formData, 'email', 'password', 'acceptedAgreementVersion'),
    }
  }

  validateStep = sectionProps => (fieldValidatorError) => {
    const { sectionIndex } = sectionProps
    if (fieldValidatorError) {
      throw new ValidationError('', { sectionIndex })
    }
  }

  validateAgreement = () => {
    const { formData: { acceptedAgreementVersion } } = this.props
    if (!acceptedAgreementVersion) throw new ValidationError()
  }

  validateEmail = () => {
    const { formData: { email } } = this.props
    validateRequiredString(email)
    validateEmail(email)
  }

  validatePassword = () => {
    const { formData: { email, password } } = this.props
    if (email) {
      validateRequiredString(password)
      validateMinLength(password, 6)
    }
  }

  activateNextSection = (sectionProps, stepValidation) => () => {
    const { onSectionActivate, sectionIndex } = sectionProps
    const { validate } = stepValidation
    if (validate().isValid) {
      onSectionActivate(sectionIndex + 1)
    }
  }

  updateFormData = (fieldValidatorProps, callback = () => {}) => () => {
    const { onFormDataUpdate } = this.props
    onFormDataUpdate(pick(this.state, 'email', 'password', 'acceptedAgreementVersion'), () => {
      if (fieldValidatorProps.validate().isValid) {
        callback()
      }
    })
  }

  handleFieldChange = fieldName => (ev) => {
    this.setState({ [fieldName]: ev.target.value })
  }

  handleEmailFocus = () => {
    this.setState({ emailFocused: true })
  }

  handleEmailBlur = emailValidation => () => {
    this.setState(
      { emailFocused: false },
      this.updateFormData(emailValidation)
    )
  }

  handleEmailEnterPress = emailValidation => () => {
    this.updateFormData(emailValidation, () => {
      this.passwordInput.focus()
    })()
  }

  handlePasswordEnterPress = (sectionProps, stepValidation, passwordValidation) => () => {
    this.updateFormData(passwordValidation, () => {
      this.agreementInput.focus()
    })()
  }

  handleFormSubmit = (ev) => {
    ev.preventDefault()
  }

  emailInputRef = (input) => {
    this.emailInput = input
  }

  passwordInputRef = (input) => {
    this.passwordInput = input
  }

  agreementInputRef = (input) => {
    this.agreementInput = input
  }

  termsInputRef = (input) => {
    this.termsInput = input
  }

  renderContent = sectionProps => (stepValidation) => {
    const { isActive } = sectionProps
    const { disabled, externalAcc, classes } = this.props
    const { email, password, emailFocused, acceptedAgreementVersion } = this.state
    const showPassword = emailFocused || !!email || !!password || !!externalAcc
    const showNextButton = !!email && !!password && !!acceptedAgreementVersion && isActive
    return (
      <StepContainer>
        <form onSubmit={this.handleFormSubmit}>
          <StepTitle>Let’s do this</StepTitle>
          <StepSpacer />
          { !!externalAcc && (
            <Fragment>
              <SocialAccount externalAcc={externalAcc} />
              <StepSpacer small />
            </Fragment>
          ) }
          <Validator rule={this.validateEmail}>
            { emailValidation => (
              <StepInput
                name="email"
                placeholder="Email address"
                disabled={disabled}
                value={email}
                inputRef={this.emailInputRef}
                validation={emailValidation}
                onChange={this.handleFieldChange('email')}
                onFocus={this.handleEmailFocus}
                onBlur={this.handleEmailBlur(emailValidation)}
                onEnterPress={this.handleEmailEnterPress(emailValidation)}
              />
            ) }
          </Validator>
          <Fade in={showPassword} appear={false} exit={false} mountOnEnter unmountOnExit>
            <div>
              <StepSpacer small />
              <Validator rule={this.validatePassword}>
                { passwordValidation => (
                  <StepInput
                    name="password"
                    type="password"
                    placeholder="Password"
                    maxLength={200}
                    disabled={disabled}
                    value={password}
                    autoComplete="new-password"
                    inputRef={this.passwordInputRef}
                    validation={passwordValidation}
                    onChange={this.handleFieldChange('password')}
                    onBlur={this.updateFormData(passwordValidation)}
                    onEnterPress={this.handlePasswordEnterPress(
                      sectionProps,
                      stepValidation,
                      passwordValidation
                    )}
                  />
                ) }
              </Validator>
            </div>
          </Fade>
          { !externalAcc && (
            <Fade in={!showPassword} appear={false} exit={false} mountOnEnter unmountOnExit>
              <div>
                <StepSpacer small />
                <SocialAccount />
              </div>
            </Fade>
          ) }
          <StepSpacer small />
          <Validator rule={this.validateAgreement}>
            { agreementValidation => (
              <FormControlLabel
                control={(
                  <Checkbox
                    inputRef={this.agreementInputRef}
                    className={agreementValidation.isInvalid ? classes.checkboxError : undefined}
                    checked={!!acceptedAgreementVersion}
                    onChange={(ev) => {
                      const { checked } = ev.target
                      this.setState({
                        acceptedAgreementVersion: checked ? config.agreementVersion : null
                      }, () => {
                        this.updateFormData(agreementValidation)()
                      })
                    }}
                  />
                )}
                label={(
                  <Typography color={agreementValidation.isInvalid ? 'error' : 'default'}>
                    I accept indemandly's
                    {' '}
                    <StyledLink external target="_blank" to={getTermsURL()}>
                      Terms of use
                    </StyledLink>
                  </Typography>
                )}
              />
            ) }
          </Validator>
          <Fade in={showNextButton}>
            <div>
              <StepSpacer small />
              <StepButton
                disabled={disabled}
                onClick={this.activateNextSection(sectionProps, stepValidation)}
              >
                next
              </StepButton>
            </div>
          </Fade>
        </form>
      </StepContainer>
    )
  }

  render() {
    const { classes, ...rest } = this.props
    return (
      <ScrollSection {...rest}>
        { sectionProps => (
          <Validator rule={this.validateStep(sectionProps)}>
            { this.renderContent(sectionProps) }
          </Validator>
        ) }
      </ScrollSection>
    )
  }
}
